<template>

	<v-form
			ref="form"
			v-model="valid"
	>

		<h2 v-if="caption">{{ caption }}</h2>

		<v-text-field
				v-model.trim="name"
				label="Customer Name"
				:maxlength="35"
				:counter="!!name"
				:rules="[
					rules.required,
					rules.maxLength(name, 35),
					rules.regex(name, rules.commonPatterns.letters),
				]"
				required
				outline
		></v-text-field>

		<v-text-field
				v-model.trim="address1"
				@change="recheckUSPS"
				label="Address Line 1"
				:maxlength="24"
				:counter="!!address1"
				:rules="[
					rules.required,
					rules.maxLength(address1, 24),
					rules.regex(address1, rules.commonPatterns.letters),
				]"
				required
				outline
		></v-text-field>

		<v-text-field
				v-model.trim="address2"
				@change="recheckUSPS"
				label="Address Line 2"
				:maxlength="24"
				:counter="!!address2"
				:rules="[
					!address2 || rules.regex(address2, rules.commonPatterns.letters),
					rules.maxLength(address2, 24),
				]"
				outline
		></v-text-field>

		<v-text-field
				v-model.trim="city"
				@change="recheckUSPS"
				label="City"
				:maxlength="30"
				:counter="!!city"
				:rules="[
					rules.required,
					rules.maxLength(city, 30),
					rules.regex(city, rules.commonPatterns.letters),
				]"
				required
				outline
		></v-text-field>

		<v-select
				v-model="state"
				@change="recheckUSPS"
				:items="states"
				label="State"
				:rules="[
					rules.required,
				]"
				required
				outline
		></v-select>

		<v-text-field
				v-model.trim="zip"
				@change="recheckUSPS"
				label="Zip Code"
				:maxlength="10"
				:counter="!!zip"
				:rules="[
					rules.required,
					rules.maxLength(zip, 10),
					rules.regex(zip, /^\d{4,5}([- ]{1}\d{4})?$/,
						'This field must be valid zip code'
					),
				]"
				validate-on-blur
				required
				outline
		></v-text-field>

		<v-text-field
				v-model="phone"
				label="Phone Number"
				mask="(###) ###-####"
				return-masked-value
				:rules="[
					rules.required,
					rules.maxLength(phone, 14),
					rules.regex(phone, /^[\d() -]{14}$/,
						'This field must be valid phone number'
					),
				]"
				validate-on-blur
				required
				outline
		></v-text-field>

		<div v-if="usps_failed">
			<v-alert value="true" outline class="white">
				We were unable to validate your address.<br>
				Please verify that you entered everything correctly and if so check.
				<v-checkbox v-model="usps_verified">
					<template v-slot:label><strong>This address is correct</strong></template>
				</v-checkbox>
			</v-alert>
		</div>
		<div v-else-if="usps_different">
			<v-alert value="true" outline class="white">
				The address you entered is different from the verified address on file with the Post Office.
				<v-radio-group v-model="choose_address">
					<v-radio value="address_usps" color="error">
						<template v-slot:label>Use verified address (<strong>Recommended</strong>)</template>
					</v-radio>
					<v-radio label="Use the address I entered" value="address_user" color="error"></v-radio>
				</v-radio-group>
			</v-alert>
		</div>

	</v-form>

</template>

<script>
	import Axios from 'axios';

	export default {
		name: "AddressForm",

		props: {
			value: {
				type: Object,
				default: (x) => {},
			},
			caption: {
				type: String,
				default: null,
			},
		},

		data() {
			return {
				valid: true,
				trigger: 'AddressForm',
				loading: false,

				usps_verified: false,
				usps_failed: false,
				usps_different: false,

				choose_address: 'address_usps',
				address_usps: {},
				address_user: {},

				name: '',
				address1: '',
				address2: '',
				city: '',
				state: '',
				states: ["AK", "AL", "AR", "AZ", "CA", "CO", "CT", "DC", "DE", "FL", "GA", "HI", "IA", "ID", "IL", "IN", "KS", "KY", "LA", "MA", "MD", "ME",
				"MI", "MN", "MO", "MS", "MT", "NC", "ND", "NE", "NH", "NJ", "NM", "NV", "NY", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT",
				"VA", "VT", "WA", "WI", "WV", "WY", "AA", "AE", "AP", "VI", "PR", "PW", "MP", "MH", "GU", "FM", "AS"],
				zip: '',
				phone: '',

				rules: {
					required: value => {
						return !!value && !!value.trim() || 'This field is required';
					},
					maxLength: (value, max) => {
						return !value || value.length <= max || `This field should not be more than ${max} characters`;
					},
					regex: (value, pattern, message) => {
						value = value.replace(/ {2,}/g, ' ');
						return pattern.test(value) || message || "This field doesn't look right";
					},
					commonPatterns: {
						letters: /[a-z\u0400-\u04FF]{2}/i,
					},
				},
			};
		},

		watch: {
			loading: function(value) {
				this.sendData({loading: value});
			},
			choose_address: function(value) {
				this.setAddress(value);
			},
			usps_different: function(value) {
				if (value) {
					this.usps_verified = true;
					this.setAddress(this.choose_address = this.$options.data().choose_address);
				}
			},
		},

		methods: {
			sendData(merge) {
				var data = {
					valid: this.valid && this.usps_verified,
					name: this.name,
					address1: this.address1,
					address2: this.address2,
					city: this.city,
					state: this.state,
					zip: this.zip,
					phone: this.phone,
				};
				if (typeof merge === 'object') {
					Object.assign(data, merge);
				}
				this.$emit('input', data);
			},

			setAddress(value) {
				const address = this[value];
				this.address1 = address.line1;
				this.address2 = address.line2;
				this.city = address.city;
				this.state = address.state;
				this.zip = address.postal_code;
			},

			recheckUSPS() {
				this.usps_verified = false;
				this.usps_failed = false;
				this.usps_different = false;
			},

			reset() {
				this.$refs.form.reset();
				Object.assign(this.$data, this.$options.data());
			},

			scrollTo(element) {
				this.$nextTick(() => {
					if (typeof element !== 'object') {
						element = this.$el.querySelector(element);
					}
					if (!!element) {
						this.$vuetify.goTo(element, {
							duration: 500,
							offset: 80,
						});
					}
				});
			},
		},

		mounted() {
			this.$eventHub.$on(this.trigger, () => {

				this.$refs.form.validate();
				this.sendData();

				if (!this.valid) {
					return this.scrollTo('.error--text');
				} else if (this.usps_verified) {
					this.reset();
					return this.scrollTo(this.$parent);
				}

				this.loading = true;

				Axios.get('/account/verify_address.php', {
					params: Object.assign(
						{return_json: true},
						this.address_user = {
							line1: this.address1,
							line2: this.address2,
							city: this.city,
							state: this.state,
							postal_code: this.zip,
						}
					),
				}).then(response => {
					const data = response.data;

					if (!!data.Error) {
						return this.$eventHub.$emit('alert',
							'Your shipping address failed to verify with the U.S. Postal Service.' +
							(!!data.ReturnText ? "\n" + data.ReturnText : ''), data.Error
						);
					} else if (this.usps_failed = !!data.long_address) {
						return this.scrollTo('.v-alert');
					}

					var different = false;
					for (const key in this.address_user) {
						if (!this.address_user.hasOwnProperty(key)) continue;

						this.address_usps[key] = data[key] || '';
						different = different || this.address_user[key] !== this.address_usps[key];
					}

					if (different) {
						this.usps_different = true;
						return this.scrollTo('.v-alert');
					}

					this.usps_verified = true;
					this.sendData({watcher: true});

				}).finally(() => {
					this.loading = false;
				});

			});
		},

		beforeDestroy() {
			this.$eventHub.$off(this.trigger);
		},

	}
</script>

<style scoped lang="less">
.v-text-field--outline {
	/deep/ .v-input__control > .v-input__slot {
		background: white !important;
		border: 0;
	}
	&:not(.v-input--is-focused):not(.v-input--has-state) {
		/deep/ .v-input__control > .v-input__slot:hover {
			border: 0;
		}
	}
}
</style>